"use client";

/* ============================================================
   TOMBOL DARURAT MENGAMBANG

   Letakkan di: app/components/TombolDaruratMengambang.jsx
   Dirender di app/layout.js, di DALAM <LanguageProvider>.

   ============================================================
   DUA JALUR, DAN KENAPA TELEPON DIDAHULUKAN

   Menu ini punya dua pilihan untuk NOMOR YANG SAMA:
     Telepon  - langsung berdering di meja IGD
     WhatsApp - pesan yang menunggu dibaca

   Keduanya TIDAK setara dalam keadaan gawat darurat, jadi tampilannya
   sengaja tidak setara juga. Telepon diberi warna penuh dan ditaruh
   paling dekat dengan jempol; WhatsApp berlatar putih di atasnya.

   Perhatikan `flexDirection: "column-reverse"` di menunya. Itu bukan
   gaya-gayaan: urutan di DOM adalah Telepon lalu WhatsApp - jadi
   pengguna keyboard dan pembaca layar menemukan Telepon lebih dulu -
   sementara di layar Telepon justru muncul di BAWAH, tepat di atas
   tombolnya, tempat yang paling mudah dijangkau jempol. Tanpa
   column-reverse, salah satu dari dua kelompok itu harus mengalah.
   ============================================================ */

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useLanguage } from "../i18n/LanguageProvider";

/* Nomor DIIMPOR, tidak disalin. Menyalin nomor telepon ke berkas kedua
   adalah cara paling umum sebuah situs berakhir dengan dua nomor yang
   berbeda: yang satu diperbarui, yang satu terlupakan. */
import { IGD, PESAN_WA } from "../layanan/igd/igd";

/* Di /layanan/igd sudah ada tombol telepon dan WhatsApp besar di
   kepala halaman. Tombol mengambang di atasnya hanya menutupi isi. */
const SEMBUNYI_DI = ["/layanan/igd"];

const TEPI = 20;
const MERAH = "#d7263d";
const MERAH_GELAP = "#b81e33";
const HIJAU_WA = "#1f8f4e";

export default function TombolDaruratMengambang() {
  const { lang } = useLanguage();
  const pathname = usePathname();

  const [buka, setBuka] = useState(false);
  const bungkusRef = useRef(null);
  const tombolRef = useRef(null);

  /* Escape menutup menu DAN mengembalikan fokus ke tombolnya. Bagian
     kedua sering dilupakan: tanpa itu, pengguna keyboard yang menekan
     Escape kehilangan posisi fokusnya dan terlempar ke awal halaman.

     Klik di luar juga menutup. Memakai "pointerdown", bukan "click" -
     click baru menyala setelah tombol dilepas, jadi menu sempat
     berkedip saat pengguna menggeser layar di ponsel. */
  useEffect(() => {
    if (!buka) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setBuka(false);
        tombolRef.current?.focus();
      }
    };
    const onLuar = (e) => {
      if (!bungkusRef.current?.contains(e.target)) setBuka(false);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onLuar);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onLuar);
    };
  }, [buka]);

  /* Menu ditutup setiap kali pindah halaman. Tanpa ini, menu yang
     terbuka ikut terbawa ke halaman berikutnya. */
  useEffect(() => setBuka(false), [pathname]);

  if (SEMBUNYI_DI.some((awalan) => pathname?.startsWith(awalan))) return null;

  const nomor = IGD.telepon.find((n) => n.utama) ?? IGD.telepon[0];
  if (!nomor?.tel) return null;

  const en = lang === "en";
  const teksTombol = en ? "Emergency" : "IGD 24 Jam";
  const labelTombol = buka
    ? en
      ? "Close emergency menu"
      : "Tutup menu darurat"
    : en
      ? "Open emergency contact menu"
      : "Buka menu kontak darurat";

  const sumberPesan = nomor.pesanWa ?? PESAN_WA;
  const pesan = encodeURIComponent(sumberPesan?.[lang] ?? sumberPesan?.id ?? "");
  const tautanWa = nomor.wa ? `https://wa.me/${nomor.wa}?text=${pesan}` : null;

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes fab-denyut {
          0%   { transform: scale(1);    opacity: 0.55; }
          70%  { transform: scale(1.55); opacity: 0;    }
          100% { transform: scale(1.55); opacity: 0;    }
        }
        @keyframes fab-muncul {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }

        .fab-denyut { animation: fab-denyut 2.4s cubic-bezier(0,0,0.2,1) infinite; }
        .fab-item   { animation: fab-muncul 180ms ease-out both; }

        @media (prefers-reduced-motion: reduce) {
          .fab-denyut { animation: none; opacity: 0; }
          .fab-item   { animation: none; }
          .fab-tombol { transition: none !important; }
        }

        /* ============================================================
           BENTUK TOMBOL - WAJIB DI KELAS, BUKAN style SEBARIS.

           Media query tidak bisa menjangkau style sebaris. Versi
           pertama komponen ini menaruh lebar dan padding di
           style={{...}}: teksnya hilang di ponsel tapi padding 18px
           tetap ada, jadi tombolnya lonjong 62x56 - bukan lingkaran.

           BAWAAN = PONSEL. Lingkaran 56px, tanpa teks. Ditulis
           mobile-first supaya kalau media query gagal dimuat, yang
           tersisa adalah bentuk yang benar untuk layar terkecil.

           56px: ambang minimum target sentuh yang lazim adalah 44px.
           Dinaikkan karena tombol ini sering ditekan sambil berjalan,
           dengan satu tangan, oleh orang yang gemetar. */
        .fab-tombol { width: 56px; padding: 0; justify-content: center; }
        .fab-teks   { display: none; }

        @media (min-width: 768px) {
          .fab-tombol { width: auto; padding: 0 18px; }
          .fab-teks   { display: inline; }
        }
      `,
        }}
      />

      {/* env(safe-area-inset-bottom): di iPhone tanpa tombol home, 20px
         dari tepi layar berarti tombolnya bertumpuk dengan garis gestur
         Safari. Tanpa baris ini tombolnya benar di simulator dan salah
         di tangan pengguna.

         z-index 40, BUKAN 9999. Lightbox foto di IgdContent harus
         berada DI ATAS tombol ini. Kalau z-index lightbox-mu lebih
         rendah dari 40, naikkan lightbox-nya - jangan turunkan ini. */}
      <div
        ref={bungkusRef}
        style={{
          position: "fixed",
          right: TEPI,
          bottom: TEPI,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          zIndex: 40,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 12,
        }}
      >
        {/* ---------- MENU ----------
           column-reverse: Telepon pertama di DOM, terakhir di layar.
           Penjelasan panjangnya ada di kepala berkas. */}
        {buka && (
          <div
            id="fab-menu-darurat"
            role="menu"
            aria-orientation="vertical"
            style={{
              display: "flex",
              flexDirection: "column-reverse",
              alignItems: "flex-end",
              gap: 10,
            }}
          >
            <ItemMenu
              href={`tel:${nomor.tel}`}
              label={en ? "Call" : "Telepon"}
              sub={nomor.tampil}
              warna={MERAH}
              teksWarna="#ffffff"
              tunda={0}
              ikon={<IkonTelepon />}
            />

            {tautanWa && (
              <ItemMenu
                href={tautanWa}
                label="WhatsApp"
                sub={nomor.tampil}
                warna="#ffffff"
                teksWarna="#1f2937"
                garis="rgba(15, 23, 42, 0.12)"
                aksen={HIJAU_WA}
                tunda={40}
                baru
                ikon={<IkonWhatsApp />}
              />
            )}
          </div>
        )}

        {/* ---------- TOMBOL ---------- */}
        <div style={{ position: "relative" }}>
          {/* Denyut dimatikan saat menu terbuka: cincin yang terus
             berdenyut di belakang menu yang sedang dibaca hanya jadi
             gangguan. aria-hidden + pointerEvents none supaya ia tidak
             pernah mencuri klik yang meleset sedikit. */}
          {!buka && (
            <span
              aria-hidden="true"
              className="fab-denyut"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 999,
                backgroundColor: MERAH,
                pointerEvents: "none",
              }}
            />
          )}

          <button
            ref={tombolRef}
            type="button"
            onClick={() => setBuka((v) => !v)}
            aria-expanded={buka}
            aria-haspopup="menu"
            aria-controls="fab-menu-darurat"
            aria-label={labelTombol}
            className="fab-tombol"
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              /* Tinggi tetap di semua layar. LEBAR dan PADDING sengaja
                 tidak ditulis di sini - diatur .fab-tombol di atas. */
              height: 56,
              border: "none",
              borderRadius: 999,
              backgroundColor: buka ? MERAH_GELAP : MERAH,
              color: "#ffffff",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: 1,
              cursor: "pointer",
              whiteSpace: "nowrap",
              /* Bayangan berlapis: satu tipis rapat untuk tepi, satu
                 lebar lembut untuk jarak. Satu bayangan besar saja
                 membuat tombolnya terlihat lepas dari halaman. */
              boxShadow:
                "0 1px 2px rgba(60,6,14,0.28), 0 10px 24px -8px rgba(60,6,14,0.45)",
              transition: "background-color 200ms ease, transform 200ms ease",
            }}
          >
            {buka ? <IkonTutup /> : <IkonAmbulans />}
            <span className="fab-teks">{teksTombol}</span>
          </button>
        </div>
      </div>
    </>
  );
}

/* ============================================================
   SATU BARIS DI DALAM MENU

   Label SELALU terlihat, termasuk di ponsel. Dua lingkaran tanpa
   tulisan tidak bisa dibedakan - dan menu yang isinya harus ditebak
   lebih buruk daripada tidak ada menu sama sekali.
   ============================================================ */
function ItemMenu({ href, label, sub, ikon, warna, teksWarna, garis, aksen, tunda, baru }) {
  return (
    <a
      role="menuitem"
      href={href}
      target={baru ? "_blank" : undefined}
      rel={baru ? "noopener noreferrer" : undefined}
      className="fab-item"
      style={{
        animationDelay: `${tunda}ms`,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        height: 48,
        padding: "0 18px",
        borderRadius: 999,
        backgroundColor: warna,
        color: teksWarna,
        border: garis ? `1px solid ${garis}` : "none",
        textDecoration: "none",
        whiteSpace: "nowrap",
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.14), 0 8px 20px -8px rgba(15,23,42,0.30)",
      }}
    >
      <span style={{ display: "inline-flex", color: aksen ?? "currentColor" }}>{ikon}</span>
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
        <span style={{ fontSize: 11.5, opacity: 0.75 }}>{sub}</span>
      </span>
    </a>
  );
}

/* ============================================================
   IKON

   Digambar di sini, bukan diimpor dari lucide-react. Alasannya bukan
   ukuran bundel - lucide sudah dipakai di tempat lain - melainkan
   karena ambulans dan WhatsApp di bawah butuh DUA warna, sementara
   ikon lucide semuanya sewarna.
   ============================================================ */

function IkonTelepon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ width: 20, height: 20, flexShrink: 0 }}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6.6 3h-3A1.6 1.6 0 0 0 2 4.6C2 13.1 8.9 20 17.4 20a1.6 1.6 0 0 0 1.6-1.6v-3a1 1 0 0 0-.8-1l-3.4-.7a1 1 0 0 0-1 .4l-1 1.4a13.6 13.6 0 0 1-5.3-5.3l1.4-1a1 1 0 0 0 .4-1L8.6 3.8a1 1 0 0 0-1-.8Z" />
    </svg>
  );
}

/* Gelembung pesan dengan gagang telepon di dalamnya.

   ⚠️ Ini BUKAN logo WhatsApp resmi, dan itu disengaja. Menggambar
   ulang logo merek dari ingatan menghasilkan bentuk yang meleset
   sedikit - cukup untuk terlihat murahan, dan tetap merupakan
   penggunaan merek dagang orang lain. Bentuk generik ini terbaca
   sebagai "pesan" tanpa mengaku-aku sebagai lambang milik siapa pun.

   Kalau kamu mau lambang aslinya, ambil dari brand kit resmi WhatsApp
   dan tukar isi fungsi ini. IgdContent.jsx juga sudah punya
   IkonWhatsApp sendiri - kalau ingin keduanya seragam, salin yang
   dari sana ke sini. */
function IkonWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 20, height: 20, flexShrink: 0 }}>
      <path d="M12 2.6a9 9 0 0 0-7.7 13.7L3 21.4l5.3-1.4A9 9 0 1 0 12 2.6Z" fill="currentColor" />
      <g
        transform="translate(7.3 6.2) scale(0.45)"
        fill="none"
        stroke="#ffffff"
        strokeWidth="4.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6.6 3h-3A1.6 1.6 0 0 0 2 4.6C2 13.1 8.9 20 17.4 20a1.6 1.6 0 0 0 1.6-1.6v-3a1 1 0 0 0-.8-1l-3.4-.7a1 1 0 0 0-1 .4l-1 1.4a13.6 13.6 0 0 1-5.3-5.3l1.4-1a1 1 0 0 0 .4-1L8.6 3.8a1 1 0 0 0-1-.8Z" />
      </g>
    </svg>
  );
}

function IkonTutup() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ width: 24, height: 24, flexShrink: 0 }}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function IkonAmbulans() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 26, height: 26, flexShrink: 0 }}>
      <rect x="1" y="7" width="13" height="8.4" rx="1.6" fill="currentColor" />
      <path d="M14 9.2h3.3a1 1 0 0 1 .75.34l2.6 2.94a1 1 0 0 1 .25.66V15.4H14Z" fill="currentColor" />
      <circle cx="6.2" cy="17.6" r="2.6" fill="currentColor" />
      <circle cx="6.2" cy="17.6" r="1.05" fill={MERAH} />
      <circle cx="17.4" cy="17.6" r="2.6" fill="currentColor" />
      <circle cx="17.4" cy="17.6" r="1.05" fill={MERAH} />
      <rect x="6.75" y="8.9" width="1.9" height="4.6" rx="0.4" fill={MERAH} />
      <rect x="5.4" y="10.25" width="4.6" height="1.9" rx="0.4" fill={MERAH} />
    </svg>
  );
}
